// =============================================================================
// appRoutes.ts — T-447 phase 3 ("take me there")
//
// The assistant answers from guides, and those guides name the screen AND its
// route (e.g. "HR > Payroll (/hr/payroll)"). This turns those mentions into real
// buttons.
//
// THE RULE: only routes in this list are ever rendered as a link. The model can
// mention anything; a button that opens a 404 would be worse than no button at
// all, so an unknown path is simply left as text. Routes with parameters
// (/projects/:id) are deliberately excluded — we don't know which record is
// meant, so guides point at the list screen and the person picks from there.
//
// When a new screen lands in App.tsx, add it here too if a guide will point at it.
// =============================================================================

export const APP_ROUTES: Record<string, string> = {
  "/": "Home",
  "/portal": "My portal",
  "/my-actions": "My actions",
  "/my-week": "My week",
  "/my-compliance": "My tickets",
  "/profile": "My profile",

  "/project": "Project hub",
  "/projects": "Projects",
  "/bids": "Bids / RFQs",
  "/project/rfq-intake": "RFQ intake",
  "/clients": "Clients",
  "/clients/new": "New client",
  "/sites": "Sites",
  "/sites/new": "New site",
  "/schedule": "Dispatch",
  "/schedule/calendar": "Calendar",
  "/schedule/classic": "Schedule (classic)",
  "/schedule/planning": "Planning board",
  "/crews": "Crews",
  "/mops": "MOPs library",
  "/acceptance": "Acceptance Centre",
  "/acceptance/defects": "Defects",
  "/packs": "Acceptance packs",
  "/client-portals": "Client portals",
  "/capture": "Capture",
  "/capture/diary": "New diary entry",

  "/finance": "Finance hub",
  "/commercial": "Commercial dashboard",
  "/commercial/premises": "Premises",
  "/quotes": "Quotes",
  "/invoices": "Invoices (AR)",
  "/pos": "Purchase Orders",
  "/pos/new": "New purchase order",
  "/rates": "Rate cards",
  "/rates/import-pdf": "Import supplier rates from PDF",
  "/opex": "OPEX",
  "/approvals": "Approvals",
  "/reports": "Reports",
  "/suppliers": "Suppliers",
  "/subbies": "Subcontractors",
  "/company-compliance": "Company compliance",
  "/finance/brands": "Brand mapping",

  "/ap": "AP / spend",
  "/ap/intake": "AP bulk intake",
  "/ap/receipts": "Receipts to code",
  "/ap/bills": "Bills to pay",
  "/ap/reimbursements": "Reimbursements",
  "/ap/register": "Receipts register",
  "/ap/xero": "Xero hand-off",
  "/ap/xero-match": "Xero match",
  "/ap/reconcile": "Bank reconcile",
  "/ap/new": "New AP invoice",
  "/receipts": "My receipts",
  "/receipts/new": "Scan a receipt",

  "/hr": "HR portal",
  "/people": "Employees",
  "/people/new": "Add employee",
  "/people/subbies/new": "Add subcontractor company",
  "/hr/team-week": "Team week",
  "/hr/timesheet-approvals": "Timesheet approvals",
  "/hr/payroll": "Payroll",
  "/hr/payroll/summary": "Payroll",
  "/hr/leave-approvals": "Leave approvals",
  "/hr/licences": "Licence oversight",
  "/hr/renewals": "Renewals",
  "/hr/compliance": "People compliance",
  "/hr/compliance/matrix": "Compliance matrix",
  "/hr/inductions": "Inductions",
  "/hr/policies": "Policies (admin)",
  "/timesheets": "My timesheet",
  "/leave": "My leave",
  "/licences": "My licences",
  "/licences/new": "Add a licence",
  "/licences/inspector": "Licence inspector view",
  "/inductions": "My inductions",
  "/policies": "My policies",
  "/equipment": "My equipment",
  "/assets": "Assets",

  "/hse": "HSE hub",
  "/hse/incidents": "Incidents",
  "/hse/standdowns": "Stand-downs & embargoes",
  "/hse/swms": "SWMS",
  "/hse/start-card": "Start card",
  "/hse/lift-plans": "Lift plans",
  "/hse/cranes": "Crane library",
  "/hse/documents": "Document library",
  "/hse/site-folder": "Site folder",
  "/hse/jsa": "JSA",
  "/permits": "Permits",
  "/jmp": "JMP",
  "/jmp/new": "New JMP",
  "/forms": "Forms",
  "/inspections": "Inspections",
  "/inspections/new": "New inspection",
  "/compliance": "Compliance",

  "/director": "Director hub",
  "/admin/users": "User accounts",
  "/admin/permissions": "Role permissions",
  "/admin/feedback": "Feedback & ideas",
  "/admin/assistant": "Assistant activity",
  "/admin/kb": "Ask Coretel guides",
};

/** Pull the app routes an answer mentions, in order, deduped. Unknown paths are
 *  dropped rather than linked — see the rule at the top of this file. */
export function extractRoutes(text: string): { path: string; label: string }[] {
  if (!text) return [];
  const found: { path: string; label: string }[] = [];
  const seen = new Set<string>();
  const re = /\/[a-z0-9][a-z0-9/-]*/gi;
  for (const raw of text.match(re) ?? []) {
    const path = raw.replace(/[).,;:`'"]+$/, "").toLowerCase();
    if (seen.has(path)) continue;
    const label = APP_ROUTES[path];
    if (!label) continue;
    seen.add(path);
    found.push({ path, label });
  }
  return found.slice(0, 4);
}
